<template>
  <div class="login-lvyun">
    <div class="login-brand">
      <div class="login-brand-inner">
        <div class="login-brand-logo">
          <span class="login-brand-name">温岭迅驰软件开发工作室</span>
        </div>
        <h1 class="login-brand-title">
          <span>餐饮ERP管理系统</span>
        </h1>
        <p class="login-brand-desc">
          专为餐饮门店打造的一体化订单、商品、随心订与会员运营解决方案
        </p>
        <ul class="login-brand-features">
          <li>
            <i class="el-icon-s-order"></i>
            <div>
              <strong>订单与发货</strong>
              <span>随心订日历、发货状态与售后流程集中管理</span>
            </div>
          </li>
          <li>
            <i class="el-icon-s-shop"></i>
            <div>
              <strong>商品与库存</strong>
              <span>门店/网店商品、库存出入库数据一目了然</span>
            </div>
          </li>
        </ul>
        <p class="login-brand-copy">© {{ copyYear }} 温岭迅驰软件开发工作室 · 餐饮后台管理系统</p>
      </div>
    </div>

    <div class="login-form-panel">
      <transition name="card-fade-scale">
        <div class="login-form-card">
          <div class="login-form-header">
            <h2>欢迎回来</h2>
            <p>登录到您的 温岭EATing创意融合轻食 管理账号</p>
          </div>

          <el-form
            :model="loginForm"
            :rules="rules"
            ref="loginForm"
            class="login-form"
            label-position="top"
            @keyup.enter.native="handleLogin"
          >
            <el-form-item label="账号" prop="username">
              <el-input
                v-model="loginForm.username"
                autocomplete="username"
                placeholder="请输入账号"
                size="large"
              />
            </el-form-item>

            <el-form-item label="密码" prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                autocomplete="current-password"
                placeholder="请输入密码"
                size="large"
                show-password
              />
            </el-form-item>

            <div class="remember-row">
              <el-checkbox v-model="loginForm.rememberMe">记住登录状态</el-checkbox>
            </div>

            <div class="slider-wrapper">
              <div
                class="slider-rail"
                ref="sliderTrack"
                :class="sliderStatus"
              >
                <div class="slider-fill" :style="{ width: sliderFillWidth + '%' }"></div>
                <span class="slider-hint">{{ sliderText }}</span>
                <div
                  class="slider-handle"
                  :class="{ success: verifySuccess }"
                  :style="{ left: handleLeft }"
                  @mousedown.prevent="onSliderDown"
                  @touchstart.prevent="onSliderDown"
                >
                  <i v-if="verifySuccess" class="el-icon-check"></i>
                  <span v-else>›</span>
                </div>
              </div>
            </div>

            <el-form-item class="btn-row">
              <el-button
                type="primary"
                size="large"
                class="login-submit-btn"
                :loading="loading"
                :disabled="!verifySuccess || loading"
                @click="handleLogin"
              >
                <i v-if="loading" class="el-icon-loading" style="margin-right: 6px;"></i>
                登录
              </el-button>
            </el-form-item>
          </el-form>

          <div class="login-form-footer">
            <p class="login-links">
              <a href="/admin/#/privacy-policy" target="_blank" rel="noopener">隐私政策</a>
              <span class="login-links-sep">|</span>
              <a href="/admin/#/user-agreement" target="_blank" rel="noopener">用户协议</a>
            </p>
            <p class="login-meta beian-inline"><beian-record-line /></p>
            <p class="login-meta">温岭迅驰软件开发工作室技术支持</p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { setAuthSession, consumeLogoutReason } from '../utils/auth'
import BeianRecordLine from '../components/BeianRecordLine.vue'

export default {
  name: 'Login',
  components: { BeianRecordLine },
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
        rememberMe: false
      },
      rules: {
        username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ]
      },
      loading: false,
      focusedField: '',
      verifySuccess: false,
      sliderStatus: 'idle',
      sliderProgress: 0,
      sliderDragging: false
    }
  },
  computed: {
    copyYear() {
      return new Date().getFullYear()
    },
    sliderFillWidth() {
      return this.sliderProgress
    },
    handleLeft() {
      if (this.verifySuccess) return 'calc(100% - 41px)'
      const p = Math.max(0, Math.min(100, Number(this.sliderProgress) || 0))
      return `calc(5px + (100% - 46px) * ${p / 100})`
    },
    sliderText() {
      if (this.sliderStatus === 'success') return '验证成功'
      if (this.sliderStatus === 'fail') return '验证失败，请重试'
      return '按住滑块，拖动到最右边'
    }
  },
  mounted() {
    document.documentElement.classList.add('admin-login-page')
    document.body.classList.add('admin-login-page')
    const reason = consumeLogoutReason()
    if (reason === 'inactive_24h' || reason === 'inactive_30m') {
      this.$message.warning('24 小时未操作，已自动退出登录，请重新登录。')
    } else if (reason === 'expired_15d') {
      this.$message.warning('登录已满 15 天，请重新登录。')
    } else if (reason === 'expired_24h') {
      this.$message.warning('登录状态已失效，请重新登录。')
    }
  },
  beforeDestroy() {
    document.documentElement.classList.remove('admin-login-page')
    document.body.classList.remove('admin-login-page')
  },
  methods: {
    fieldError(field) {
      const form = this.$refs.loginForm
      if (!form || !form.fields) return false
      const item = form.fields.find((f) => f.prop === field)
      return item && item.validateState === 'error'
    },
    onSliderDown(event) {
      if (this.verifySuccess) return
      this.sliderDragging = true
      this.sliderStatus = 'idle'

      const MAX_PERCENT = 92

      const moveHandler = (e) => {
        if (!this.sliderDragging) return
        const clientX = e.touches ? e.touches[0].clientX : e.clientX
        const track = this.$refs.sliderTrack
        if (!track) return
        const rect = track.getBoundingClientRect()
        let percent = ((clientX - rect.left) / rect.width) * 100
        if (percent < 0) percent = 0
        if (percent > 100) percent = 100
        this.sliderProgress = percent
      }

      const upHandler = () => {
        if (!this.sliderDragging) return
        this.sliderDragging = false
        if (this.sliderProgress >= MAX_PERCENT) {
          this.sliderProgress = 100
          this.verifySuccess = true
          this.sliderStatus = 'success'
        } else {
          this.sliderStatus = 'fail'
          this.sliderProgress = 0
          setTimeout(() => {
            if (!this.verifySuccess) {
              this.sliderStatus = 'idle'
            }
          }, 800)
        }
        window.removeEventListener('mousemove', moveHandler)
        window.removeEventListener('mouseup', upHandler)
        window.removeEventListener('touchmove', moveHandler)
        window.removeEventListener('touchend', upHandler)
      }

      window.addEventListener('mousemove', moveHandler)
      window.addEventListener('mouseup', upHandler)
      window.addEventListener('touchmove', moveHandler)
      window.addEventListener('touchend', upHandler)
    },
    handleLogin() {
      if (!this.verifySuccess) {
        this.$message.error('请先完成滑块拖动验证')
        return
      }
      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) return
        this.loading = true
        try {
          const resp = await this.$api.post('/admin/login', {
            username: String(this.loginForm.username || '').trim(),
            password: this.loginForm.password
          })
          if (resp && resp.code === 200 && resp.data) {
            const d = resp.data
            // 后端 MySQL 不可达时会返回 code=200、data=[]，不能当登录成功
            if (Array.isArray(d) || typeof d !== 'object' || !d.token) {
              this.$message.error(
                (resp && resp.message) || '登录失败：数据库未连接或未返回凭证，请检查服务器 MySQL 与 backend/.env'
              )
              return
            }
            const roleLabel = d.role || ''
            let role = 'staff'
            if (roleLabel === '管理员' || roleLabel === '超级管理员') role = 'admin'
            else if (roleLabel === '收银员') role = 'cashier'
            const perms = Array.isArray(d.permissions) ? d.permissions : []
            const userInfo = {
              username: d.username,
              displayName: d.name || d.username,
              employeeId: d.employeeId != null && d.employeeId !== '' ? d.employeeId : d.id,
              id: d.id != null ? d.id : '',
              userId: d.userId != null && d.userId !== '' ? String(d.userId) : (d.user_id != null ? String(d.user_id) : ''),
              phone: d.phone || '',
              role,
              roleLabel,
              permissions: perms.length ? perms : [],
              avatarUrl: d.avatar_url || d.avatarUrl || '',
              loginTime: new Date().toISOString()
            }
            setAuthSession({
              userInfo,
              isLoggedIn: true,
              token: d.token,
              rememberMe: !!this.loginForm.rememberMe
            })
            this.$message.success((resp && resp.message) || '登录成功！')
            const landing =
              roleLabel === '员工' || roleLabel === '收银员'
                ? '/orders'
                : '/'
            this.$router.replace(landing).catch(() => {})
            this.$nextTick(() => {
              if (window.location.hash === '#/login') {
                window.location.hash = `#${landing}`
              }
            })
          } else {
            this.$message.error((resp && resp.message) || '登录失败')
          }
        } catch (e) {
          const status = e && e.response && e.response.status
          if (status === 404) {
            this.$message.error(
              '登录接口 404：请检查 Nginx 是否将 /api 转发到 Node（勿写成 proxy_pass …3000/；推荐 …3000/api/），并确认服务器已更新 backend 且已重启。可在浏览器打开 /api/admin/ping 自检。'
            )
            return
          }
          const data = e && e.response && e.response.data
          const msg =
            (data && typeof data === 'object' && data.message) ||
            e._networkHint ||
            '登录失败，请检查账号密码或网络'
          this.$message.error(typeof msg === 'string' ? msg : '登录失败')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.login-lvyun {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  max-height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
  font-family: var(--app-font);
  background: #0c1118;
}

.login-brand {
  flex: 1 1 50%;
  min-width: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  background:
    radial-gradient(120% 80% at 10% 0%, rgba(43, 182, 115, 0.22), transparent 50%),
    radial-gradient(80% 60% at 90% 80%, rgba(15, 23, 42, 0.55), transparent 55%),
    linear-gradient(160deg, #141b24 0%, #0c1118 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  align-self: stretch;
  box-sizing: border-box;
}

.login-brand-inner {
  width: 100%;
  max-width: 680px;
  height: 100%;
  margin: 0;
  padding: 48px 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-brand-logo {
  display: flex;
  align-items: center;
  margin-bottom: 36px;
}

.login-brand-name {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.login-brand-title {
  margin: 0 0 16px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.02em;
}

.login-brand-title span {
  display: block;
}

.login-brand-desc {
  margin: 0 0 32px;
  font-size: 14px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.62);
  white-space: nowrap;
}

.login-brand-features {
  list-style: none;
  margin: 0 0 40px;
  padding: 0;
}

.login-brand-features li {
  display: flex;
  gap: 14px;
  margin-bottom: 22px;
  align-items: flex-start;
}

.login-brand-features i {
  font-size: 22px;
  color: #4ade80;
  margin-top: 2px;
}

.login-brand-features strong {
  display: block;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.login-brand-features span {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
}

.login-brand-copy {
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
}

.login-form-panel {
  flex: 1 1 50%;
  min-width: 0;
  height: 100%;
  margin: 0;
  padding: 40px 32px;
  background:
    radial-gradient(700px 360px at 80% 0%, rgba(43, 182, 115, 0.08), transparent 50%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  box-sizing: border-box;
  overflow: auto;
}

.login-form-card {
  width: 100%;
  max-width: 400px;
  padding: 28px 28px 24px;
  border-radius: 22px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 24px 48px rgba(15, 23, 42, 0.1);
}

.login-form-header h2 {
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
  font-family: var(--app-font);
}

.login-form-header p {
  margin: 0 0 28px;
  font-size: 14px;
  color: #64748b;
}

.login-form ::v-deep(.el-form-item__label) {
  color: #333333;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.4;
  padding-bottom: 6px;
}

.login-form ::v-deep(.el-input__inner) {
  border-radius: 12px;
  height: 44px;
  line-height: 44px;
  border-color: #e0e0e0;
  background: #fafafa;
}

.login-form ::v-deep(.el-input__inner:focus) {
  border-color: #121212;
  background: #ffffff;
}

.slider-wrapper {
  margin-bottom: 10px;
}

.remember-row {
  margin: -2px 0 12px;
  color: #64748b;
  font-size: 13px;
}

.slider-rail {
  position: relative;
  width: 100%;
  height: 46px;
  border-radius: 14px;
  background: #f1f5f9;
  border: 1px solid rgba(15, 23, 42, 0.1);
  overflow: hidden;
  user-select: none;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.slider-rail.success {
  border-color: rgba(43, 182, 115, 0.45);
  background: #ecfdf5;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.12);
}

.slider-rail.fail {
  border-color: rgba(225, 29, 72, 0.4);
  background: #fff1f2;
  box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.08);
}

.slider-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.88), rgba(30, 41, 59, 0.92));
  transition: width 0.12s ease-out;
  border-radius: 14px 0 0 14px;
}

.slider-rail.success .slider-fill {
  background: linear-gradient(90deg, #22a06b, #2bb673);
  width: 100% !important;
  border-radius: 14px;
}

.slider-hint {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.02em;
  pointer-events: none;
  transition: color 0.2s ease;
}

.slider-rail.success .slider-hint {
  color: #ffffff;
}

.slider-rail.fail .slider-hint {
  color: #be123c;
}

.slider-handle {
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.28);
  cursor: grab;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.slider-handle:active {
  cursor: grabbing;
  transform: scale(0.98);
}

.slider-handle.success {
  background: #ffffff;
  color: #047857;
  box-shadow: 0 4px 14px rgba(43, 182, 115, 0.35);
}

.slider-handle .el-icon-check {
  font-size: 18px;
  font-weight: 700;
}

.btn-row {
  margin-top: 12px;
  margin-bottom: 0;
}

.login-submit-btn {
  width: 100%;
  height: 46px !important;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.12em;
  border-radius: 12px !important;
}

.login-form-footer {
  margin-top: 28px;
  text-align: center;
}

.login-meta {
  font-size: 12px;
  color: #bbbbbb;
  margin: 0 0 8px;
  line-height: 1.5;
}

.login-links {
  margin: 0;
  font-size: 13px;
  color: #999999;
}

.login-links a {
  color: #666666;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-links a:hover {
  color: #121212;
  text-decoration: underline;
}

.login-links-sep {
  margin: 0 8px;
  color: #dddddd;
}

.beian-inline {
  white-space: nowrap;
}

.card-fade-scale-enter-active {
  animation: card-in 0.45s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes card-in {
  0% {
    opacity: 0;
    transform: scale(0.98) translateY(8px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 960px) {
  .login-lvyun {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    max-height: none;
    overflow: auto;
  }

  .login-brand {
    padding: 0;
    flex: none;
    height: auto;
    min-height: 280px;
  }

  .login-brand-inner {
    max-width: none;
    height: auto;
    padding: 32px 24px;
  }

  .login-brand-desc {
    white-space: normal;
  }

  .login-form-panel {
    flex: 1;
    height: auto;
    padding: 32px 20px 40px;
  }
}
</style>

<style>
/* 仅登录页：全屏铺满，消除 html/body 白边 */
html.admin-login-page,
html.admin-login-page body {
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
  background: #121212 !important;
}
html.admin-login-page #app,
html.admin-login-page body > #app {
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  background: #121212 !important;
  overflow: hidden !important;
}
</style>
